/**
 * @file Squelette procedural LEGER du chat (os logiques = Object3D pivots) : corps, tete, 2 oreilles, queue 3 segments, 4 pattes, ancrage d'arme.
 */
import type { Object3D } from 'three';

/**
 * Hierarchie (repere local : +Y haut, +Z avant du chat, unites monde ; hauteur totale ~1.2) :
 *
 *   root (position/orientation monde, cap = direction de deplacement)
 *   └─ hips (pivot de rebond / squash)
 *      ├─ torso (maillage)                     ├─ legBL / legBR (pattes arriere, pivot hanche)
 *      ├─ tailBase → tailMid → tailTip         └─ upper (pivot buste : tourne vers la VISEE, independamment du cap)
 *      │                                          ├─ head → earL, earR, hatMount, eyes
 *      │                                          ├─ armL / armR (pattes avant, pivots epaules) + handL / handR (mains)
 *      │                                          └─ weaponMount (entre les pattes avant ; recul = translation -Z locale)
 *      │                                             └─ (arme de GunFactory) → muzzle
 */
export interface CatBones {
  root: Object3D;
  hips: Object3D;
  torso: Object3D;
  upper: Object3D;
  head: Object3D;
  earL: Object3D;
  earR: Object3D;
  tail: [Object3D, Object3D, Object3D];
  armL: Object3D;
  armR: Object3D;
  legBL: Object3D;
  legBR: Object3D;
  weaponMount: Object3D;
  hatMount: Object3D;
  /** Mains blanches (enfants de `upper`, positionnees par l'animateur : autour de l'arme en tir, aux extremites des bras quand l'arme est rangee). */
  handL: Object3D;
  handR: Object3D;
}

/** Cotes du squelette partages entre la fabrique (geometries des bras) et l'animateur (pose « arme rangee »). Repere de `upper`. */
export const CAT_LAYOUT = {
  /** Epaules (x = +gauche du chat). */
  shoulder: { x: 0.27, y: 0.02, z: 0.03 },
  /** Ancrage d'arme en position de tir : entre les pattes avant. */
  mount: { x: 0, y: -0.1, z: 0.38 },
  /** Position des « mains » par rapport a l'ancrage. */
  handX: 0.1,
  handY: 0.02,
} as const;

export interface CatRig {
  bones: CatBones;
  /** Bouche du canon de l'arme courante (null sans arme) : position du muzzle flash. */
  muzzle: Object3D | null;
  /** Arme actuellement tenue (defId) pour savoir quand reconstruire. */
  weaponDefId: string | null;
  skinId: string;
  /** Pose de repos de chaque os (rotations initiales) : l'animateur compose par-dessus. */
  rest: Map<Object3D, { rx: number; ry: number; rz: number; px: number; py: number; pz: number }>;
}

/** Etat d'animation lu dans le core a chaque frame (aucune logique de jeu dans le rendu). */
export interface CatAnimInput {
  moving: boolean;
  /** Vitesse normalisee 0..1 (deplacement de la frame / vitesse max). */
  speed01: number;
  /** Cap du corps (radians monde, convention core : 0 = +x). */
  heading: number;
  /** Angle de visee (radians monde). */
  aimAngle: number;
  /** Compteur de tirs du core : un changement declenche recul + flash. */
  shotSeq: number;
  /** Recul de l'arme (unites) selon le comportement (BehaviorParams.kickback). */
  kickback: number;
  /** Arme rangee (hub) : tenue d'une main le long de la hanche, fondu lisse vers/depuis la pose de tir. */
  stowed?: boolean;
  time: number;
  dt: number;
}

/** Memoire d'animation par chat (phases d'oscillateurs, recul en cours, timers de tressautement d'oreille). */
export interface CatAnimState {
  lastShotSeq: number;
  kick: number;
  flash: number;
  walkPhase: number;
  earTimer: number;
  earTwitch: number;
  earSide: -1 | 1;
  /** Graine de desynchronisation entre chats. */
  phaseOffset: number;
  upperYaw: number;
  /** Fondu 0..1 vers la pose « arme rangee ». */
  stow: number;
}

export const newAnimState = (phaseOffset: number): CatAnimState => ({
  lastShotSeq: 0,
  kick: 0,
  flash: 0,
  walkPhase: 0,
  earTimer: 2 + phaseOffset * 3,
  earTwitch: 0,
  earSide: 1,
  phaseOffset,
  upperYaw: 0,
  stow: 0,
});
